import React from 'react';
import {BaseComponent as Component} from 'BaseComponent';
import Const from 'constants';
import {SearchRuleBuilder as RuleBuilder} from 'services/search-rule-builder';
import PlayerPoints from 'components/molecules/player-points';
import PlayerExposure from 'components/molecules/player-exposure';
import GeneralExposure from 'components/molecules/player-exposure/general-exposure';
import {filterObjectByParams, sortArrayOfObjects} from 'helpers/common-helper';
import {getOpponentsDelimiterByPlayer} from 'helpers/player-helper';
import DisabledPlayerIfGameLockedSpecification from 'specifications/disabled-player-if-game-locked-specification';
import PlayersGroupExclusionSwitcher from 'components/molecules/player-exclusion/players-group-exclusion-switcher';
import CorrelationStackingAllPlayersSwitcher from 'components/molecules/player-correlation-stacking/correlation-stacking-all-players-switcher';
import PlayerTeamStackingAllPlayersSwitcher from 'components/molecules/player-team-stacking/player-team-stacking-all-players-switcher';
import PlayerSympathy from 'components/molecules/player-sympathy';
import {generateRandomKey} from 'helpers/hash';
import {splitByCapitalLetter} from 'helpers/string-helper';
import DisabledPlayerIfGameInactiveSpecification from 'specifications/disabled-player-if-game-inactive-specification';

class BasePlayersList extends Component {
    constructor(props) {
        super(props);
        this.resetSettingsSignature = null;
        this.isValueBeingChanged = {};
        this.state = this.getBaseState();
    }

    getBaseState(config = {}) {
        const state = {
            sortBy: {
                col: 'salary',
                direction: 'desc',
            },
        };
        return {...state, ...config};
    }

    wasSettingsReset(obj) {
        return obj.signature !== this.resetSettingsSignature;
    }

    updateResetSettingsSignature(obj) {
        this.resetSettingsSignature = obj.signature;
    }

    resetBaseState(config = {}) {
        this.setState(this.getBaseState(config));
    }

    getPlayersForRendering() {
        const {players: rawPlayers} = this.props;

        let players = this.getFilteredPlayers(rawPlayers.all());
        players = this.getSortedPlayers(players);

        return [...players];
    }

    sortBy(e, sortBy) {
        e.preventDefault();

        if (typeof sortBy === 'object') {
            return this.setState({sortBy});
        }

        const {col, direction} = this.state.sortBy;

        this.setState({
            sortBy: {
                col: sortBy,
                direction: col === sortBy && direction === 'desc' ? 'asc' : 'desc',
            },
        });
    }

    getSortColumn() {
        const {sortBy} = this.state;

        return sortBy.col === 'projected_ownership' ? (obj) => obj.projOwn.getValue() : sortBy.col;
    }

    getSortDirection() {
        const {sortBy} = this.state;

        return sortBy.direction;
    }

    getSearchConditions() {
        return {};
    }

    getFilteredPlayers(players) {
        const searchRules = this.getSearchConditions();

        return filterObjectByParams(players, searchRules, true);
    }

    getSortedPlayers(players) {
        const sortColumn = this.getSortColumn();
        const sortDirection = this.getSortDirection();

        return sortArrayOfObjects(players, sortColumn, sortDirection);
    }

    getSortDirectionClassName(col) {
        if (this.state.sortBy.col === col) {
            return this.state.sortBy.direction;
        }

        return '';
    }

    getActualValue(player, param) {
        let value = player[param];
        if (this.isValueBeingChanged[param] && player.playerId in this.state[param]) {
            value = this.state[param][player.playerId];
        }

        return value;
    }

    isOverallMode(mode) {
        return mode === Const.OVERALL_MODE;
    }

    isRankMode(mode) {
        return inArray(mode, [Const.CAPTAIN_MODE, Const.STAR_MODE, Const.PRO_MODE, Const.FLEX_MODE]);
    }

    isExcludedMode(mode) {
        return mode === Const.EXCLUDED_MODE;
    }

    isNotExcludedMode(mode) {
        return !this.isExcludedMode(mode);
    }

    isLockedMode(mode) {
        return mode === Const.LOCKED_MODE;
    }

    getRenderKey() {
        return generateRandomKey();
    }

    getSearchRuleByMode(params = {}) {
        const {playersFilter} = this.props;

        let rule = {};

        if (playersFilter.playersType === Const.OVERALL_MODE) {
            rule = this.getSearchRuleByOverallMode(params);
        } else if (
            inArray(playersFilter.playersType, [Const.CAPTAIN_MODE, Const.STAR_MODE, Const.PRO_MODE, Const.FLEX_MODE])
        ) {
            rule = this.getSearchRuleByRankMode(params);
        } else if (playersFilter.playersType === Const.FAVORITE_MODE) {
            rule = this.getSearchRuleByFavoriteMode();
        } else if (playersFilter.playersType === Const.EXCLUDED_MODE) {
            rule = this.getSearchRuleByExcludedMode();
        } else if (playersFilter.playersType === Const.LOCKED_MODE) {
            rule = this.getSearchRuleByLockedMode();
        }

        return {...rule};
    }

    getSearchRuleByOverallMode(params = {}) {
        const {overallMode = {}} = params;
        const {checkPointsExclusion = false, checkSalaryExclusion = false} = overallMode;
        const rule = RuleBuilder.callback(
            'playerId',
            (player) =>
                !player.isExcludedAnywhere({
                    checkUserExclusion: false,
                    checkPointsExclusion,
                    checkSalaryExclusion,
                }),
        );

        return {...rule};
    }

    getSearchRuleByRankMode(params = {}) {
        const {salaryMode = Const.FLEX_MODE} = params;
        const {playersFilter} = this.props;
        const rule = RuleBuilder.callback(
            'playerId',
            (player) =>
                !player.isExcludedByMode(playersFilter.playersType, {
                    salaryMode,
                }),
        );

        return {...rule};
    }

    getSearchRuleByCallback(params = {}) {
        const {propName, callback} = params;

        const rule = RuleBuilder.callback(propName, callback);

        return {...rule};
    }

    getSearchRuleByFavoriteMode() {
        const rule = RuleBuilder.callback('playerId', (player) => !player.isExcludedAnywhere());

        return {...rule};
    }

    getSearchRuleByExcludedMode() {
        const rule = RuleBuilder.callback('playerId', (player) => player.isExcludedAnywhere());

        return {...rule};
    }

    getSearchRuleByLockedMode() {
        const {playersLock} = this.props;

        const rule = RuleBuilder.inArr('playerId', playersLock.getMergedLockedPlayerIds().map(String), (item) =>
            String(item),
        );

        return {...rule};
    }

    getSearchRuleByPlayerName() {
        const {playersFilter} = this.props;
        const rule = RuleBuilder.like('name', playersFilter.playerName);

        return {...rule};
    }

    getSearchRuleByTeamName() {
        const {playersFilter} = this.props;

        let rule = {};
        if (playersFilter.team === Const.PLAYERS_TEAM_FILTER_ALL) {
            rule = RuleBuilder.inArr('team', playersFilter.games);
        } else {
            rule = RuleBuilder.equal('team', playersFilter.team);
        }

        return {...rule};
    }

    getSearchRuleByPosition() {
        const {playersFilter} = this.props;

        return RuleBuilder.inArr('position', playersFilter.playerPosition.getValue(), (item) => parseInt(item));
    }

    getSearchRuleByMultiPosition() {
        const {playersFilter} = this.props;

        return RuleBuilder.arrHasNoDuplicates('position', playersFilter.playerPosition.getValue());
    }

    getSearchRuleByConfirmed() {
        const {playersFilter} = this.props;
        const rule = RuleBuilder.equal('confirmed', playersFilter.playerConfirmed, (item) => String(item));

        return {...rule};
    }

    getSearchRuleByOrder() {
        const {mlbFilters} = this.props;
        const orderNumbers = mlbFilters.getNotChosen();

        let rule = {};
        if (orderNumbers.length) {
            rule = RuleBuilder.notInArr('order', orderNumbers, (item) => item.isPitcher());
        }

        return {...rule};
    }

    getSearchRuleByTiers() {
        const {tiersFilter} = this.props;

        let value = tiersFilter.getValue();
        if (Array.isArray(value)) {
            value = value.map(String);
        }

        const rule = RuleBuilder.arrHasNoDuplicates('rosterPosition', value, (item) => item.map(String));

        return {...rule};
    }

    getSearchRuleByLineGroup() {
        const {lineGroupFilter} = this.props;

        const rule = RuleBuilder.notInArr('userLineGroup', lineGroupFilter.getNotChosen(), (item) => item.isGoalie());

        return {...rule};
    }

    getEnabledPlayers(players) {
        const {games, sportConfig} = this.props;

        return sportConfig.isModeLateswap() ? players.filter((player) => !games.isPlayerGameLocked(player)) : players;
    }

    getPlayerSympathyComponent(params = {}) {
        const {player} = params;
        const {mode = player.getMode()} = params;

        return <PlayerSympathy playerId={player.getId()} mode={mode} />;
    }

    getPlayerPointsComponent(params = {}) {
        const {player, propName = 'userPoints', className = 'form-control small-table-input'} = params;

        return (
            <PlayerPoints className={className} playerId={player.getId()} mode={player.getMode()} propName={propName} />
        );
    }

    wrapContentInTooltip(content, tooltipSubject) {
        const contentWithTooltip = React.cloneElement(content, {tooltipSubject});

        return <>{contentWithTooltip}</>;
    }

    getPlayerExposureComponent(params = {}) {
        const {playerId, mode = Const.FLEX_MODE, propName, inputClassName = 'small-table-input'} = params;

        return (
            <PlayerExposure
                className={inputClassName}
                playerId={playerId}
                mode={mode}
                propName={propName}
                testId={`${splitByCapitalLetter(propName)}-input`}
            />
        );
    }

    getPlayersGroupExclusionSwitcherComponent(players, label) {
        const enabledPlayers = this.getEnabledPlayers(players);

        return <PlayersGroupExclusionSwitcher players={enabledPlayers} label={label} />;
    }

    getCorrelationStackingAllPlayersSwitcherComponent(players, label) {
        const enabledPlayers = this.getEnabledPlayers(players);

        return (
            <div>
                <CorrelationStackingAllPlayersSwitcher players={enabledPlayers} className={'chain-btn'} />
            </div>
        );
    }

    getPlayerTeamStackingAllPlayersSwitcherComponent(players, label) {
        const enabledPlayers = this.getEnabledPlayers(players);

        return <PlayerTeamStackingAllPlayersSwitcher players={enabledPlayers} label={label} className={'chain-btn'} />;
    }

    getGeneralExposureComponent(params = {}) {
        const {modesConfig} = this.props;
        const {
            players,
            mode = Const.FLEX_MODE,
            propName,
            outerClassName = 'percent-in',
            inputClassName = 'form-control small-table-input',
        } = params;

        const enabledPlayers = this.getEnabledPlayers(players);

        let modes = [mode];
        if (inArray(mode, [Const.EXCLUDED_MODE])) {
            modes = modesConfig.all({asArr: true}).map((modeConfig) => modeConfig.getRole());
        }

        return (
            <div className={outerClassName}>
                <GeneralExposure
                    className={inputClassName}
                    players={enabledPlayers}
                    modes={modes}
                    propName={propName}
                />
                <span className="percent">%</span>
            </div>
        );
    }

    getOpponentsDelimiter(player) {
        return getOpponentsDelimiterByPlayer(player);
    }

    getSharpPointsCell(params = {}) {
        const {points, isCalculatedFromProps} = params;
        const bgColor = isCalculatedFromProps ? {} : {'--_cell-color': 'rgb(250, 240, 150)'};

        return (
            <td className="color-cell" style={{...bgColor}}>
                <span>{points}</span>
            </td>
        );
    }

    getFinalPointsBlock(params = {}) {
        const {points, className} = params;
        const {sportConfig} = this.props;

        if (sportConfig.isModeBacktest()) {
            return (
                <td className={className}>
                    <span>{points}</span>
                </td>
            );
        }

        return null;
    }

    getHeader(params = {}) {
        const {propName = '', title = ''} = params;

        return (
            <th>
                <a
                    className={this.getSortDirectionClassName(propName)}
                    href="#"
                    onClick={(e) => ::this.sortBy(e, propName)}
                >
                    {title}
                </a>
            </th>
        );
    }

    getSharpPointsHeader() {
        return this.getHeader({
            propName: 'sharpPoints',
            title: 'Props Proj',
        });
    }

    getFinalPointsHeaderSort() {
        const {sportConfig} = this.props;

        if (sportConfig.isModeBacktest()) {
            return (
                <th>
                    <a
                        className={this.getSortDirectionClassName('fantasyPoints')}
                        href="#"
                        onClick={(e) => ::this.sortBy(e, 'fantasyPoints')}
                    >
                        Final FPts
                    </a>
                </th>
            );
        }

        return null;
    }

    getFinalPointsHeader() {
        if (this.isMainMode()) return;

        return (
            <th>
                <a href="#">Final FPts</a>
            </th>
        );
    }

    getFinalPointsHeaderMobile(params = {}) {
        const {className} = params;

        if (this.isMainMode()) {
            return null;
        }

        return <th className={className}>Final FPts</th>;
    }

    isMainMode() {
        const {sportConfig} = this.props;

        return sportConfig.isModeMain();
    }

    getRowClassName(player) {
        const {sportConfig, games = {}} = this.props;

        if (DisabledPlayerIfGameLockedSpecification.isSatisfiedBy({sportConfig, games, player})) {
            return 'disabled-player';
        }

        if (DisabledPlayerIfGameInactiveSpecification.isSatisfiedBy({sportConfig, games, player})) {
            return 'inactive-game';
        }

        return '';
    }
}

export {BasePlayersList};
